import { ClearFiltersIcon, SearchIcon, SelectIcon, SettingsIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useIntentEpics, useIntentFeatures } from '@/hooks'
import { cn } from '@/lib/utils'
import { INTENT_DEFAULT_SORT_DIRECTIONS, intentStatusFilterOptions, type IntentSortColumn } from '@/types/intent'
import { X } from 'lucide-react'

export type IntentColumn = 'number' | 'name' | 'epic' | 'feature' | 'status' | 'created'

interface IntentsFiltersProps {
  projectId: string
  search: string
  onSearchChange: (value: string) => void
  epicFilter: string
  onEpicFilterChange: (value: string) => void
  featureFilter: string
  onFeatureFilterChange: (value: string) => void
  statuses: string[]
  onStatusesChange: (value: string[]) => void
  showCheckboxes: boolean
  onToggleCheckboxes: () => void
  visibleColumns: IntentColumn[]
  onToggleColumn: (column: IntentColumn) => void
  onClearAll: () => void
  sortBy: IntentSortColumn
  sortDirection: 'asc' | 'desc'
}

const columnLabels: Record<IntentColumn, string> = {
  number: 'ID',
  name: 'Name',
  epic: 'Epic',
  feature: 'Feature',
  status: 'Status',
  created: 'Created',
}

export function IntentsFilters({
  projectId,
  search,
  onSearchChange,
  epicFilter,
  onEpicFilterChange,
  featureFilter,
  onFeatureFilterChange,
  statuses,
  onStatusesChange,
  showCheckboxes,
  onToggleCheckboxes,
  visibleColumns,
  onToggleColumn,
  onClearAll,
  sortBy,
  sortDirection,
}: IntentsFiltersProps) {
  const { data: epics = [] } = useIntentEpics(projectId)
  const { data: features = [] } = useIntentFeatures(projectId)
  const hasActiveSort = sortBy !== 'number' || sortDirection !== INTENT_DEFAULT_SORT_DIRECTIONS.number
  const hasActiveFilters = search !== '' || epicFilter !== 'all' || featureFilter !== 'all' || statuses.length > 0 || hasActiveSort

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-[2] min-w-0">
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-card-foreground" />
        <Input
          placeholder="Search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-10 rounded-lg border-border pl-9 pr-9 py-2 text-sm tracking-[0.015em] placeholder:text-[rgba(145,149,167,1)]"
          aria-label="Search intents"
          data-testid="intents-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Select value={epicFilter} onValueChange={onEpicFilterChange}>
        <SelectTrigger className="flex-1 min-w-[150px] rounded-lg border-border" aria-label="Filter by epic">
          <SelectValue placeholder="All Epics" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Epics</SelectItem>
          {epics.filter(Boolean).map((epic) => (
            <SelectItem key={epic} value={epic}>
              {epic}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={featureFilter} onValueChange={onFeatureFilterChange}>
        <SelectTrigger className="flex-1 min-w-[150px] rounded-lg border-border" aria-label="Filter by feature">
          <SelectValue placeholder="All Features" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Features</SelectItem>
          {features.filter(Boolean).map((feature) => (
            <SelectItem key={feature} value={feature}>
              {feature}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <MultiSelect
        options={intentStatusFilterOptions}
        value={statuses}
        onChange={onStatusesChange}
        placeholder="All Statuses"
        displayMode="compact"
        searchable={false}
        showCompactIcon={false}
        showCompactOverflowCount
        className="w-[200px]"
        aria-label="Filter by status"
      />

      <Button
        type="button"
        variant="ghost"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex items-center gap-1.5 text-sm font-medium"
        aria-label="Clear all filters"
      >
        <ClearFiltersIcon className="h-4 w-4" />
        Clear All
      </Button>

      <div className="ml-auto flex items-center gap-3">
        <Button
          type="button"
          variant={showCheckboxes ? 'default' : 'outline'}
          onClick={onToggleCheckboxes}
          aria-label="Toggle multi-select"
          className={cn(
            'flex h-10 w-10 min-h-[40px] min-w-[40px] items-center justify-center rounded-lg border-2 border-border-accent p-0',
            showCheckboxes ? 'bg-primary text-primary-foreground' : 'bg-toolbar'
          )}
        >
          <SelectIcon className="h-5 w-5" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-label="Toggle columns"
              className="flex h-10 min-w-[40px] items-center gap-2.5 rounded-lg border-1 border-border-accent bg-toolbar text-card-foreground px-4 py-2.5 hover:bg-muted hover:text-foreground"
            >
              <SettingsIcon className="h-4 w-4" />
              <span className="text-sm font-medium">Columns</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(Object.keys(columnLabels) as IntentColumn[]).map((col) => (
              <DropdownMenuCheckboxItem
                key={col}
                checked={visibleColumns.includes(col)}
                onCheckedChange={() => onToggleColumn(col)}
              >
                {columnLabels[col]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
